import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import SelectInput from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import { TextInputField } from '@oberoninternal/travelbase-ds/components/form/TextInput';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Box } from '@rebass/grid';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as Yup from 'yup';
import messages from '../constants/messages';
import { Formable } from '../entities/Formable';
import Fieldset from './designsystem/Fieldset';
import StyledCaret from './designsystem/StyledCaret';
import Formed, { Handler } from './Formed';
import Stack from './Stack';
import { LeaveActivityReviewInput } from '../generated/graphql';
import { useTenantContext } from '../context/TenantContext';
import { getReviewOptions } from './ReviewModal';

export type Values = Formable<Omit<LeaveActivityReviewInput, 'ticketId'>>;

const initialValues: Values = {
    title: '',
    ratingActivity: '',
    ratingHospitality: '',
    ratingValueForMoney: '',
    comment: '',
    tips: '',
};

interface Props {
    handleSubmit: Handler<Values>;
    activityName: string | undefined;
}

const ActivityReviewForm: FC<React.PropsWithChildren<Props>> = ({ handleSubmit, activityName }) => {
    const intl = useIntl();
    const { brandConfig } = useTenantContext();
    const reviewOptions = getReviewOptions(intl);

    const validationSchema = Yup.object().shape({
        title: Yup.string()
            .max(65, intl.formatMessage(messages.maximumCharsError, { maxLength: 65 }))
            .required(intl.formatMessage(messages.fieldError)),
        ratingActivity: Yup.number().required(intl.formatMessage(messages.fieldError)),
        ratingHospitality: Yup.number().required(intl.formatMessage(messages.fieldError)),
        ratingValueForMoney: Yup.number().required(intl.formatMessage(messages.fieldError)),
        comment: Yup.string()
            .max(500, intl.formatMessage(messages.maximumCharsError, { maxLength: 500 }))
            .required(intl.formatMessage(messages.fieldError)),
        tips: Yup.string(),
    });

    return (
        <Formed<Values> initialValues={initialValues} validationSchema={validationSchema} handleSubmit={handleSubmit}>
            {({ isSubmitting }) => (
                <>
                    <BoldBody>
                        <FormattedMessage
                            defaultMessage="Beoordeel {activity}"
                            values={{ activity: activityName ?? 'Activiteit' }}
                        />
                    </BoldBody>

                    <Stack mt="2.4rem" spacing={3} mb={[0, 7]}>
                        <Fieldset
                            variant="large"
                            label={<FormattedMessage defaultMessage="Hoe beoordeel je de activiteit?" />}
                            id="ratingActivity"
                        >
                            <ReviewSelectInput
                                name="ratingActivity"
                                options={reviewOptions}
                                hideChoices
                                ToggleComponent={StyledCaret}
                                placeholder={<FormattedMessage defaultMessage="Kies een cijfer" />}
                            />
                        </Fieldset>

                        <Fieldset
                            variant="large"
                            label={
                                <FormattedMessage defaultMessage="Hoe beoordeel je de organisatie (onvangst/begeleiding) bij de activiteit?" />
                            }
                            id="ratingHospitality"
                        >
                            <ReviewSelectInput
                                name="ratingHospitality"
                                options={reviewOptions}
                                hideChoices
                                ToggleComponent={StyledCaret}
                                placeholder={<FormattedMessage defaultMessage="Kies een cijfer" />}
                            />
                        </Fieldset>

                        <Fieldset
                            variant="large"
                            label={
                                <FormattedMessage defaultMessage="Hoe beoordeel je de prijs in verhouding to wat je kreeg?" />
                            }
                            id="ratingValueForMoney"
                        >
                            <ReviewSelectInput
                                name="ratingValueForMoney"
                                options={reviewOptions}
                                hideChoices
                                ToggleComponent={StyledCaret}
                                placeholder={<FormattedMessage defaultMessage="Kies een cijfer" />}
                            />
                        </Fieldset>
                    </Stack>
                    <Box mb={[2, 5]}>
                        <BoldBody>
                            <FormattedMessage defaultMessage="Omschrijf jouw ervaring in één korte zin." />
                        </BoldBody>
                        <SummaryInputField name="title" />
                        <NeutralBody variant="small">
                            <FormattedMessage
                                defaultMessage="We plaatsen deze zin als ''titel van jouw ervaring'' boven je beoordeling. Bijvoorbeeld: “Veel gelachen en geleerd” of “Bijzonder om {partner} zo te ontdekken”"
                                values={{ partner: brandConfig.name }}
                            />
                        </NeutralBody>
                    </Box>
                    <Box mb={7}>
                        <BoldBody>
                            <FormattedMessage defaultMessage="Toelichting" />
                        </BoldBody>
                        <TextArea name="comment" />
                        <NeutralBody variant="small">
                            <FormattedMessage
                                defaultMessage="Hier vertel je meer over je ervaring. Bijvoorbeeld: ''We hebben een deel van {partner} gezien dat we als ervaren {partner} gangers nooit hadden bezocht” of ''Je hebt echt een paar goeie schoenen nodig voor deze wandeling''."
                                values={{
                                    partner: brandConfig.name,
                                }}
                            />
                        </NeutralBody>
                    </Box>

                    <Box>
                        <BoldBody>
                            <FormattedMessage defaultMessage="Tip voor de organisator" />
                        </BoldBody>
                        <TextArea name="tips" />
                        <NeutralBody variant="small">
                            <FormattedMessage
                                defaultMessage="Deze tip sturen we direct door naar de organisator van de activiteit en tonen we niet op de website. Bijvoorbeeld: “We konden de gids niet altijd goed verstaan” of ''De workshop duurde vrijlang''."
                                values={{
                                    br: <br />,
                                }}
                            />
                        </NeutralBody>
                    </Box>

                    <PublishButton size="large" type="submit" submitting={isSubmitting}>
                        <FormattedMessage defaultMessage="Beoordeling publiceren" />
                    </PublishButton>
                    <NeutralBody variant="small">
                        <FormattedMessage defaultMessage="Je beoordeling is direct zichtbaar op de website" />
                    </NeutralBody>
                </>
            )}
        </Formed>
    );
};

const NeutralBody = styled(Body)`
    color: ${({ theme }) => theme.colors.neutral['50']};
    margin-bottom: 2rem;
`;

const BoldBody = styled(Body)`
    font-weight: 600;
    margin-bottom: ${({ theme }) => theme.spacing['20_Tiny']};
`;

const PublishButton = styled(Button)`
    margin-bottom: 1rem;
    margin-top: ${({ theme }) => theme.spacing['50_Semi']};
    width: 100%;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xs}) {
        width: unset;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        margin-top: ${({ theme }) => theme.spacing['80_XXLarge']};
    }
`;

const TextArea = styled(TextInputField).attrs({ type: 'textarea' })`
    min-height: 19.2rem;
`;

const ReviewSelectInput = styled(SelectInput)`
    max-width: 20.8rem;
`;

const SummaryInputField = styled(TextInputField)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        min-height: 14.4rem;
    }
`;
export default ActivityReviewForm;
